import React, { ReactNode } from 'react';
import {
    CaseReport,
    saveReport,
} from '../../../services/reportService';
import { DocumentProvider, useDocument } from '../../documents/DocumentContext';
import type { AutoSaveStatus } from '../../../hooks/useAutoSave';
import type { DocumentStatus } from '../../documents/types';

interface ReportData {
    [key: string]: any;
}

export interface ActionResult {
    success: boolean;
    error?: string;
}

interface ReportContextType {
    data: ReportData;
    updateData: (key: string, value: any) => void;
    saveData: () => Promise<ActionResult>;
    completeReport: () => Promise<ActionResult>;
    isSaving: boolean;
    isLoading: boolean;
    caseRecordId?: string;
    reportStatus?: DocumentStatus;
    setValue?: (key: string, value: any) => void;
    autoSaveStatus: AutoSaveStatus;
    lastSavedAt: Date | null;
}

interface ReportProviderProps {
    children: ReactNode;
    initialData?: ReportData;
    schemaKey: string;
    schemaVersion?: number;
    caseRecordId?: string;
    reportData?: { data?: ReportData; status?: DocumentStatus };
    onPersisted?: (savedCase: CaseReport) => Promise<void> | void;
    onDataChange?: (data: ReportData) => void;
    persistDataOverrides?: Record<string, any>;
}

export const ReportProvider: React.FC<ReportProviderProps> = ({
    children,
    initialData = {},
    schemaKey,
    schemaVersion,
    caseRecordId,
    reportData,
    onPersisted,
    onDataChange,
    persistDataOverrides,
}) => {
    if (!schemaKey) {
        throw new Error('ReportProvider requires schemaKey');
    }

    return (
        <DocumentProvider
            initialData={initialData}
            documentId={caseRecordId}
            documentData={reportData}
            onDataChange={onDataChange}
            saveDocument={async (data, currentId) => {
                const payload = {
                    ...data,
                    ...(persistDataOverrides || {}),
                };
                const savedReport = await saveReport(
                    payload,
                    currentId,
                    payload.title || 'Draft Report',
                    'draft',
                    schemaKey,
                    schemaVersion
                );
                await onPersisted?.(savedReport);
                return {
                    documentId: savedReport._id,
                    status: 'draft' as const,
                };
            }}
            completeDocument={async (data, currentId) => {
                const payload = {
                    ...data,
                    ...(persistDataOverrides || {}),
                };
                const savedReport = await saveReport(
                    payload,
                    currentId,
                    payload.title || 'Completed Report',
                    'completed',
                    schemaKey,
                    schemaVersion
                );
                await onPersisted?.(savedReport);
                return {
                    documentId: savedReport._id,
                    status: 'completed' as const,
                };
            }}
        >
            {children}
        </DocumentProvider>
    );
};

export const useReport = (): ReportContextType => {
    const context = useDocument();
    return {
        data: context.data,
        updateData: context.updateData,
        saveData: context.save,
        completeReport: context.complete,
        isSaving: context.isSaving,
        isLoading: context.isLoading,
        caseRecordId: context.documentId,
        reportStatus: context.documentStatus,
        setValue: context.setValue,
        autoSaveStatus: context.autoSaveStatus,
        lastSavedAt: context.lastSavedAt,
    };
};
